import React, { useState, useEffect } from 'react';
import { ExportField, ExportFieldsResponse, FIELD_CATEGORIES } from '../types/exportFields';
import { QueryFilters } from '../types';
import { api } from '../lib/api';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (selectedFields: string[], filters: QueryFilters) => Promise<void>;
  filters: QueryFilters;
  totalCount: number;
  isExporting: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  filters,
  totalCount,
  isExporting
}) => {
  const [availableFields, setAvailableFields] = useState<ExportField[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load available export fields from backend
  useEffect(() => {
    if (isOpen) {
      loadExportFields();
    }
  }, [isOpen]);

  const loadExportFields = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getExportFields();
      
      if (response.data) {
        setAvailableFields(response.data.fields);
        setSelectedFields(response.data.defaultFields);
      } else {
        throw new Error('No export fields data received');
      }
    } catch (err) {
      console.error('Error loading export fields:', err);
      setError(err instanceof Error ? err.message : 'Failed to load export fields');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldKey)
        ? prev.filter(key => key !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleCategoryToggle = (category: keyof typeof FIELD_CATEGORIES) => {
    const categoryFields = availableFields
      .filter(field => field.category === category)
      .map(field => field.key);
    
    const allSelected = categoryFields.every(key => selectedFields.includes(key));
    
    if (allSelected) {
      // Deselect all fields in category
      setSelectedFields(prev => prev.filter(key => !categoryFields.includes(key)));
    } else {
      // Select all fields in category
      setSelectedFields(prev => [...new Set([...prev, ...categoryFields])]);
    }
  };

  const handleSelectAll = () => {
    setSelectedFields(availableFields.map(field => field.key));
  };

  const handleSelectDefaults = () => {
    setSelectedFields(availableFields.filter(field => field.default).map(field => field.key));
  };

  const handleClearAll = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }
    
    try {
      await onExport(selectedFields, filters);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const groupedFields = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, ExportField[]>);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Export CSV - {totalCount.toLocaleString()} Records
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isExporting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading export fields...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Quick Actions */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleSelectDefaults}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    Select Defaults ({availableFields.filter(f => f.default).length})
                  </button>
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  >
                    Select All ({availableFields.length})
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedFields.length} of {availableFields.length} fields
                </p>
              </div>

              {/* Field Categories */}
              <div className="space-y-6">
                {Object.entries(FIELD_CATEGORIES).map(([categoryKey, categoryLabel]) => {
                  const categoryFields = groupedFields[categoryKey] || [];
                  if (categoryFields.length === 0) return null;

                  const selectedInCategory = categoryFields.filter(field => 
                    selectedFields.includes(field.key)
                  ).length;
                  const allSelected = selectedInCategory === categoryFields.length;
                  const someSelected = selectedInCategory > 0 && selectedInCategory < categoryFields.length;

                  return (
                    <div key={categoryKey} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-gray-900">{categoryLabel}</h4>
                        <button
                          onClick={() => handleCategoryToggle(categoryKey as keyof typeof FIELD_CATEGORIES)}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${
                            allSelected 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {allSelected ? 'Deselect All' : 'Select All'} ({selectedInCategory}/{categoryFields.length})
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {categoryFields.map(field => (
                          <label
                            key={field.key}
                            className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFields.includes(field.key)}
                              onChange={() => handleFieldToggle(field.key)}
                              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900">
                                {field.label}
                                {field.default && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              {field.description && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {field.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedFields.length > 0 ? (
                `Ready to export ${selectedFields.length} fields for ${totalCount.toLocaleString()} records`
              ) : (
                'Select at least one field to export'
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedFields.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
